"use client";

import { useEffect, useRef, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { useInView } from "framer-motion";
import dayjs from "dayjs";
import { sbBlok } from "@/types/sb-types";
import { useWindowSize } from "@/hooks/useWindowSize";
import {
  ENTRY_FEE,
  INITIAL_PURSE,
  INITIAL_PURSE_ELEVATED,
} from "@/utils/constants";
import { Tournament } from "@/types/tournament";
import { isElevatedRound } from "@/utils/tournaments";
import clsx from "clsx";

interface TeaserRowProps {
  blok: sbBlok;
  tournament: Tournament;
}

const TeaserRow = ({ blok, tournament }: TeaserRowProps) => {
  const [isHovered, setHovered] = useState(false);
  const teaserRowRef = useRef(null);
  const isInView = useInView(teaserRowRef);
  const { isDesktop, isTablet, isMobile } = useWindowSize();

  const playersCount =
    tournament?.participants?.allInvitationsCount -
    tournament?.participants?.declinedCount -
    tournament?.participants?.withdrawnCount;

  const isElevated =
    tournament && isElevatedRound(tournament.season, tournament.name);
  const isFinalSeason2 =
    tournament &&
    tournament.season === 2 &&
    tournament.name?.toLowerCase()?.includes("final");

  const TOTAL_PRIZE = isFinalSeason2
    ? INITIAL_PURSE
    : (isElevated ? INITIAL_PURSE_ELEVATED : INITIAL_PURSE) +
      ENTRY_FEE * playersCount;

  const teaserRowProps = {
    tournament: {
      label_col_1: tournament?.name,
      label_col_2: "Purse",
      label_col_3: "Players",
      info_col_1: `${dayjs(tournament?.startDate).format("D MMM")} - ${dayjs(
        tournament?.endDate
      ).format("D MMM")}`,
      info_col_2: TOTAL_PRIZE.toLocaleString("en-US"),
      info_col_3: playersCount,
    },
    storyblok: { ...blok },
  };
  const teaserRowData =
    tournament &&
    Object.keys(tournament).length !== 0 &&
    blok?.is_auto_generated_content
      ? teaserRowProps.tournament
      : teaserRowProps.storyblok;

  const teaserElementTransition = {
    transition: "all 0.8s",
  };

  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const animateTeaserElement =
    (isDesktop && isHovered) ||
    (isTablet && isInView) ||
    (isMobile && isInView);

  if (isFirstRender) return <div ref={teaserRowRef} />;

  return (
    <div
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      <div
        className={clsx("grid-container bg-tm-c-gray-950 py-[60px] px-0", {
          "mt-7 lg:mt-14":
            !!blok?.margin_top?.length &&
            blok?.margin_top[0].option === "large",
          "mt-14 lg:mt-[120px]":
            !!blok?.margin_top?.length &&
            blok?.margin_top[0].option === "x_large",
          "mt-[120px] lg:mt-[180px]":
            !!blok?.margin_top?.length &&
            blok?.margin_top[0].option === "xx_large",
          "mb-7 lg:mb-14":
            !!blok?.margin_bottom?.length &&
            blok?.margin_bottom[0].option === "large",
          "mb-14 lg:mb-[120px]":
            !!blok?.margin_bottom?.length &&
            blok?.margin_bottom[0].option === "x_large",
          "mb-[120px] lg:mb-[180px]":
            !!blok?.margin_bottom?.length &&
            blok?.margin_bottom[0].option === "xx_large",
        })}
      >
        <div className="col-start-1 col-span-4 row-start-1 lg:col-start-2 lg:col-span-14 2xl:col-start-5 2xl:col-span-24">
          <div {...storyblokEditable(blok)}>
            <h2 className="text-center mx-0 mt-0 mb-5 lg:mb-7">
              {blok?.teaser_headline}
            </h2>
            <div
              className="flex flex-col items-center justify-center gap-2.5 md:flex-row lg:gap-5"
              ref={teaserRowRef}
            >
              <div
                className="flex flex-col items-center justify-center w-[250px] h-[130px] 
    rounded-[10px] bg-gradient-to-b from-[#1c1c1c] to-[#2a2a2a] 
    py-0 px-[30px] text-center lg:w-[300px] lg:h-[150px] min-[1601px]:w-[360px] min-[1601px]:h-[200px]"
                style={{
                  transform: animateTeaserElement
                    ? "translate(0,0)"
                    : `translate(${
                        isDesktop ? "-40px" : isTablet ? "-20px" : 0
                      }, ${isMobile ? "20px" : 0})`,
                  opacity: animateTeaserElement ? 1 : 0.5,
                  ...teaserElementTransition,
                }}
              >
                {teaserRowData?.info_col_1 && (
                  <p className="text-[15px] m-0 lg:text-xl">
                    {teaserRowData.info_col_1}
                  </p>
                )}
                {teaserRowData?.label_col_1 && (
                  <p className="text-[15px] m-0 opacity-50 lg:text-xl">
                    {teaserRowData.label_col_1}
                  </p>
                )}
              </div>
              <div
                className="flex flex-col items-center justify-center w-[250px] h-[130px] 
    rounded-[10px] bg-gradient-to-b from-[#1c1c1c] to-[#2a2a2a] 
    py-0 px-[30px] text-center lg:w-[300px] lg:h-[150px] min-[1601px]:w-[360px] min-[1601px]:h-[200px]"
                style={{
                  transform: animateTeaserElement
                    ? "translate(0,0)"
                    : `translate(0, ${isMobile ? "40px" : 0})`,
                  opacity: animateTeaserElement ? 1 : 0.5,
                  ...teaserElementTransition,
                }}
              >
                {teaserRowData?.info_col_2 && (
                  <p className="text-[15px] m-0 lg:text-xl">
                    {teaserRowData.info_col_2}
                  </p>
                )}
                {teaserRowData?.label_col_2 && (
                  <p className="text-[15px] m-0 opacity-50 lg:text-xl">
                    {teaserRowData.label_col_2}
                  </p>
                )}
              </div>
              <div
                className="flex flex-col items-center justify-center w-[250px] h-[130px] 
    rounded-[10px] bg-gradient-to-b from-[#1c1c1c] to-[#2a2a2a] 
    py-0 px-[30px] text-center lg:w-[300px] lg:h-[150px] min-[1601px]:w-[360px] min-[1601px]:h-[200px]"
                style={{
                  transform: animateTeaserElement
                    ? "translate(0,0)"
                    : `translate(${
                        isDesktop ? "40px" : isTablet ? "20px" : 0
                      }, ${isMobile ? "6rem" : 0})`,
                  opacity: animateTeaserElement ? 1 : 0.5,
                  ...teaserElementTransition,
                }}
              >
                {teaserRowData?.info_col_3 && (
                  <p className="text-[15px] m-0 lg:text-xl">
                    {teaserRowData.info_col_3}
                  </p>
                )}
                {teaserRowData?.label_col_3 && (
                  <p className="text-[15px] m-0 opacity-50 lg:text-xl">
                    {teaserRowData.label_col_3}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeaserRow;
